import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import toast from "react-hot-toast";
import { RiEyeLine, RiPencilLine } from "react-icons/ri";
import { BsFillTrashFill } from "react-icons/bs";
import PopupModal from "../../PopupModal";
import {
  deleteResumeImportCandidate,
  saveResumeImportCandidate,
  saveResumes,
} from "../../../api/services/hroneApi";
import {
  DISPLAY_MISSING_FIELD_LABELS,
  IMPORT_STATUS,
  buildCreateCandidatePayload,
  computeImportCounts,
  getDisplayMissingFields,
  isPdfResumeUrl,
  normalizeApplication,
} from "../../../util/hiringUtils";
import { fetchImportTabData } from "../../../api/services/resumeImportService";
import { updateImportState } from "../../../util/resumeImportStorage";
import { usePermissions } from "../../../redux/slices/permissionsSlice";
import { FEATURE_CODES } from "../../../util/featureCodes";
import {
  ImportStatusBadge,
  ResumeImportReviewDrawer,
  buildReviewFormFromCandidate,
} from "../ResumeImportShared";
import "../ImportResumesModal/index.css";

const ResumeImportPanel = ({
  jobId,
  status,
  sessionId,
  primaryColor,
  onSaved,
  onCountsChange,
  jobClosed = false,
}) => {
  const [candidates, setCandidates]     = useState([]);
  const [failedFiles, setFailedFiles]     = useState([]);
  const [loading, setLoading]             = useState(true);
  const [reviewCand, setReviewCand]       = useState(null);
  const [reviewForm, setReviewForm]       = useState(null);
  const [reviewMode, setReviewMode]       = useState("edit");
  const [isReviewSaving, setIsReviewSaving] = useState(false);
  const [serverFieldErrors, setServerFieldErrors] = useState({});
  const [resumePreview, setResumePreview] = useState({ open: false, url: null, name: "" });

  const { hasPermission } = usePermissions();
  const canEditCandidate = hasPermission(FEATURE_CODES.HIRING_CANDIDATE_EDIT);
  const canDeleteCandidate = hasPermission(FEATURE_CODES.HIRING_CANDIDATE_DELETE);

  const isFailedTab = status === IMPORT_STATUS.FAILED;

  const onCountsChangeRef = useRef(onCountsChange);
  onCountsChangeRef.current = onCountsChange;

  const syncLocalState = useCallback((nextCandidates, nextFailed) => {
    updateImportState(jobId, (current) => {
      const otherPending = (current.pendingCandidates || []).filter(
        (c) => c.importStatus !== status
      );
      const mergedPending = isFailedTab
        ? otherPending
        : [...otherPending, ...nextCandidates];

      const counts = computeImportCounts(mergedPending, nextFailed);
      onCountsChangeRef.current?.(status, isFailedTab ? nextFailed.length : nextCandidates.length);

      return {
        ...current,
        pendingCandidates: mergedPending,
        failedFiles: isFailedTab ? nextFailed : (current.failedFiles || []),
        counts,
      };
    });
  }, [jobId, status, isFailedTab]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const result = await fetchImportTabData(jobId, status, sessionId);
        if (cancelled) return;

        if (isFailedTab) {
          setFailedFiles(result.failedFiles);
          setCandidates([]);
          onCountsChangeRef.current?.(status, result.failedFiles.length);
        } else {
          setCandidates(result.candidates);
          setFailedFiles([]);
          onCountsChangeRef.current?.(status, result.candidates.length);
        }
      } catch {
        if (!cancelled) toast.error("Failed to load import data");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [jobId, status, sessionId, isFailedTab]);

  const openView = useCallback((c) => {
    setResumePreview({ open: true, url: c.resumeUrl || null, name: c.name || c.resumeName || "Resume" });
  }, []);

  const openReview = useCallback((c) => {
    setReviewCand(c);
    setReviewForm(buildReviewFormFromCandidate(c));
    // Without hiring.candidate.edit the review drawer opens read-only.
    setReviewMode(canEditCandidate ? "edit" : "view");
    setServerFieldErrors({});
  }, [canEditCandidate]);

  const openDetails = useCallback((c) => {
    setReviewCand(c);
    setReviewForm(buildReviewFormFromCandidate(c));
    setReviewMode("view");
    setServerFieldErrors({});
  }, []);

  const closeReview = () => {
    setReviewCand(null);
    setReviewForm(null);
    setServerFieldErrors({});
  };

  const handleRowClicked = useCallback((params) => {
    if (params.event?.target?.closest?.("[data-no-row-click]")) return;
    if (!jobClosed && status === IMPORT_STATUS.NEEDS_REVIEW) openReview(params.data);
    else openDetails(params.data);
  }, [status, jobClosed, openReview, openDetails]);

  const removeCandidate = useCallback(async (candidate) => {
    if (!canDeleteCandidate) return;
    try {
      const res = await deleteResumeImportCandidate(jobId, candidate.id, { session_id: sessionId });
      if (res.ok) {
        const data = await res.json();
        if (data?.status === false) throw new Error(data.message || "Remove failed");
      }
    } catch (err) {
      if (err.message && !String(err.message).includes("404")) {
        toast.error(err.message || "Failed to remove");
        return;
      }
    }

    // Use the functional updater so this always filters against the latest
    // state — not the `candidates` array captured when this closure was
    // created. Without this, rapid consecutive deletes (before a re-render
    // happens in between) all read the same stale list, and whichever
    // setCandidates call resolves last silently reintroduces the row(s)
    // removed by the other in-flight delete(s).
    setCandidates((prev) => {
      const next = prev.filter((c) => c.id !== candidate.id);
      syncLocalState(next, failedFiles);
      return next;
    });
    if (reviewCand?.id === candidate.id) closeReview();
    toast.success("Removed");
  }, [canDeleteCandidate, jobId, sessionId, failedFiles, reviewCand, syncLocalState]);

  const removeFailedFile = async (file) => {
    try {
      await deleteResumeImportCandidate(jobId, file.id, { session_id: sessionId });
    } catch {
      // allow local removal
    }
    setFailedFiles((prev) => {
      const next = prev.filter((f) => f.id !== file.id);
      syncLocalState(candidates, next);
      return next;
    });
    toast.success("Removed");
  };

  const updateReviewForm = (key, val) => {
    setReviewForm((prev) => ({ ...prev, [key]: val }));
    if (serverFieldErrors[key]) {
      setServerFieldErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const saveReviewed = async () => {
    if (!reviewForm || isReviewSaving || !reviewCand) return;

    setIsReviewSaving(true);
    setServerFieldErrors({});
    try {
      let saved = null;

      // Use the candidate's OWN originating session (captured at parse time),
      // not the panel's "currently active" sessionId prop — Needs Review rows
      // are fetched job-scoped from the DB and can belong to an older import
      // batch than whatever session is active right now. Branching on the
      // wrong (stale) session sends it to the bulk endpoint with a session_id
      // that doesn't own this candidate, which the backend 404s.
      const candidateSessionId = reviewCand.sessionId || null;

      if (candidateSessionId && reviewCand.tempId) {
        const candidatePayload = buildCreateCandidatePayload(reviewForm, {
          parseId: reviewCand.parseId,
          tempId:  reviewCand.tempId,
        });
        const res = await saveResumes(jobId, {
          session_id: candidateSessionId,
          candidates: [candidatePayload],
        });
        const data = await res.json();
        if (!data.status) throw new Error(data.message || data.detail || "Save failed");
        saved = normalizeApplication(
          data.data?.applications?.[0] || data.data?.candidates?.[0] || data.data
        );
      } else {
        // This candidate wasn't loaded from a live import session (e.g. fetched
        // job-scoped from the DB) — no session_id/parse_id/temp_id to send, just
        // the reviewed candidate fields the /save endpoint actually expects.
        const candidatePayload = buildCreateCandidatePayload(reviewForm);
        const res = await saveResumeImportCandidate(jobId, reviewCand.id, candidatePayload);
        const data = await res.json();
        if (!data.status) throw new Error(data.message || data.detail || "Save failed");
        saved = normalizeApplication(
          data.data?.application || data.data?.applications?.[0] || data.data
        );
      }

      if (saved) onSaved?.([saved]);

      const nextCandidates = candidates.filter((c) => c.id !== reviewCand.id);
      setCandidates(nextCandidates);

      updateImportState(jobId, (current) => {
        const savedTempIds = [...(current.savedTempIds || [])];
        if (reviewCand.tempId && !savedTempIds.includes(reviewCand.tempId)) {
          savedTempIds.push(reviewCand.tempId);
        }
        const pendingCandidates = (current.pendingCandidates || []).filter(
          (c) => c.id !== reviewCand.id
        );
        const counts = computeImportCounts(pendingCandidates, current.failedFiles || []);
        onCountsChangeRef.current?.(status, nextCandidates.length);
        return { ...current, savedTempIds, pendingCandidates, counts };
      });

      toast.success("Candidate saved successfully");
      closeReview();
    } catch (err) {
      const message = err.message || "Failed to save candidate";
      if (/email|phone/i.test(message)) {
        setServerFieldErrors({ email: message });
      } else {
        toast.error(message);
      }
    } finally {
      setIsReviewSaving(false);
    }
  };

  const columnDefs = useMemo(() => [
    { headerName: "CANDIDATE NAME",  field: "name",            pinned: "left", width: 180, filter: true, floatingFilter: true, suppressMenu: true },
    { headerName: "EMAIL",           field: "email",           width: 220, filter: true, floatingFilter: true, suppressMenu: true },
    { headerName: "PHONE",           field: "phone",           width: 150, filter: true, floatingFilter: true, suppressMenu: true },
    { headerName: "EXPERIENCE",      field: "experience",      width: 130, filter: true, floatingFilter: true, suppressMenu: true },
    {
      headerName: "ATS SCORE", field: "atsScore", width: 130,
      sortable: true, filter: true, floatingFilter: true, suppressMenu: true,
      cellRenderer: (p) => `${p.data.atsScore || 0}%`,
    },
    {
      headerName: "RECOMMENDATION", field: "aiRecommendation", width: 280,
      filter: true, floatingFilter: true, suppressMenu: true,
      wrapText: true, autoHeight: true,
      cellStyle: { whiteSpace: "normal", lineHeight: "18px", padding: "8px 0" },
      tooltipValueGetter: (p) => p.data.aiRecommendation,
    },
    {
      headerName: "STATUS", field: "importStatus", width: 140,
      filter: true, floatingFilter: true, suppressMenu: true,
      cellStyle: { display: "flex", alignItems: "center" },
      cellRenderer: (p) => <ImportStatusBadge status={p.data.importStatus} />,
    },
    ...(status === IMPORT_STATUS.NEEDS_REVIEW ? [{
      headerName: "MISSING FIELDS", width: 220, suppressMenu: true,
      cellStyle: { display: "flex", alignItems: "center" },
      cellRenderer: (p) => {
        const missing = getDisplayMissingFields(p.data).map((k) => DISPLAY_MISSING_FIELD_LABELS[k]);
        if (!missing.length) return <span style={{ color: "#9e9e9e" }}>—</span>;
        return <span style={{ color: "#dc3545", fontSize: "13px" }}>{missing.join(", ")}</span>;
      },
    }] : []),
    {
      headerName: "ACTIONS", width: 110, suppressMenu: true,
      cellStyle: { display: "flex", alignItems: "center", gap: "12px" },
      cellRenderer: (p) => (
        <div data-no-row-click="true" style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <RiEyeLine
            title="View resume"
            style={{ fontSize: "17px", color: "#1565c0", cursor: "pointer" }}
            onClick={() => p.context.openView(p.data)}
          />
          {!jobClosed && status === IMPORT_STATUS.NEEDS_REVIEW && canEditCandidate && (
            <RiPencilLine
              title="Edit candidate"
              style={{ fontSize: "17px", color: "#28a745", cursor: "pointer" }}
              onClick={() => p.context.openReview(p.data)}
            />
          )}
          {canDeleteCandidate && (
            <BsFillTrashFill
              title="Remove"
              style={{ fontSize: "14px", color: "#dc3545", cursor: "pointer" }}
              onClick={() => p.context.removeCandidate(p.data)}
            />
          )}
        </div>
      ),
    },
  ], [status, jobClosed, canEditCandidate, canDeleteCandidate]);

  const defaultColDef = useMemo(() => ({ cellStyle: { fontSize: "14px" } }), []);

  const gridContext = useMemo(() => ({
    openView,
    openReview,
    removeCandidate,
  }), [openView, openReview, removeCandidate]);

  if (loading) {
    return (
      <div style={{ padding: "60px 20px", textAlign: "center", color: "#9e9e9e" }}>
        <p style={{ fontSize: "15px" }}>Loading…</p>
      </div>
    );
  }

  if (isFailedTab) {
    if (!failedFiles.length) {
      return (
        <div style={{ padding: "60px 20px", textAlign: "center", color: "#646569" }}>
          <p style={{ fontSize: "15px" }}>No failed files.</p>
        </div>
      );
    }

    return (
      <div className="job-details-grid-wrapper">
        <div className="ir-failed-section" style={{ margin: 0 }}>
          <table className="ir-table">
            <thead>
              <tr>
                <th>File Name</th>
                <th>Failure Reason</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {failedFiles.map((f) => (
                <tr key={f.id}>
                  <td className="ir-failed-filename">{f.fileName}</td>
                  <td className="ir-failed-reason">{f.reason}</td>
                  <td>
                    <button
                      type="button"
                      className="ir-inline-btn ir-inline-btn--danger"
                      onClick={() => removeFailedFile(f)}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  if (!candidates.length) {
    const emptyLabels = {
      [IMPORT_STATUS.NEEDS_REVIEW]: "No candidates need review.",
      [IMPORT_STATUS.DUPLICATE]:    "No duplicate candidates.",
    };
    return (
      <div style={{ padding: "60px 20px", textAlign: "center", color: "#646569" }}>
        <p style={{ fontSize: "15px" }}>{emptyLabels[status] || "No candidates in this category."}</p>
      </div>
    );
  }

  return (
    <>
      <div className="job-details-grid-wrapper">
        <div
          className="ag-theme-alpine"
          style={{ height: "60vh", fontSize: "14px", fontFamily: "Arial, Helvetica, sans-serif" }}
        >
          <AgGridReact
            rowData={candidates}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            context={gridContext}
            getRowId={(p) => p.data.id}
            pagination
            paginationPageSize={20}
            suppressCellFocus
            suppressRowClickSelection
            enableCellTextSelection={false}
            enableBrowserTooltips
            onRowClicked={handleRowClicked}
            rowStyle={{ cursor: "pointer" }}
          />
        </div>
      </div>

      {reviewCand && reviewForm && (
        <ResumeImportReviewDrawer
          candidate={reviewCand}
          form={reviewForm}
          onChange={updateReviewForm}
          onSave={saveReviewed}
          onClose={closeReview}
          isSaving={isReviewSaving}
          primaryColor={primaryColor}
          viewOnly={reviewMode === "view"}
          editable={!jobClosed && status === IMPORT_STATUS.NEEDS_REVIEW}
          onRequestEdit={() => setReviewMode("edit")}
          serverErrors={serverFieldErrors}
        />
      )}

      {resumePreview.open && (
        <PopupModal
          showModal={resumePreview.open}
          modalName={`${resumePreview.name} — Resume`}
          closeBtn={() => setResumePreview({ open: false, url: null, name: "" })}
          width="min(90vw, 980px)"
        >
          {resumePreview.url && isPdfResumeUrl(resumePreview.url, resumePreview.name) ? (
            <iframe
              src={`${resumePreview.url}#navpanes=0`}
              title="Resume Preview"
              style={{ width: "100%", height: "75vh", border: "none", display: "block" }}
            />
          ) : (
            <div style={{ padding: "40px 20px", textAlign: "center", color: "#646569" }}>
              {resumePreview.url
                ? "Only PDF files can be viewed here. DOC/DOCX resumes cannot be previewed in the browser."
                : "No resume available for this candidate."}
            </div>
          )}
        </PopupModal>
      )}
    </>
  );
};

export default ResumeImportPanel;
